import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import InputAdornment from "@mui/material/InputAdornment";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import GroupRoundedIcon from "@mui/icons-material/GroupRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import { usersApi } from "../../../actions";
import { brand, notice } from "../../../theme/businessFlowsTheme";
import { STAFF_ROLES, canPlatform, staffRoleOf, workspaceRoleOf } from "../../../utils/permissions";
import { EmptyState, ListSkeleton, Refusal, Surface, Toast, focusRing, menuPaper, rowSx } from "../../workspaces/WorkspaceUi";
import { fullName, initials } from "../../workspaces/workspaceFormat";

const FILTERS = [
  { key: "", label: "Everyone" },
  { key: "none", label: "No workspace" },
  { key: "expired", label: "Expired trial" },
  { key: "staff", label: "bpExperts" },
  { key: "several", label: "Several workspaces" },
];

// Console → All users: everyone across workspaces, their workspace and their
// bpExperts role. Replaces the Users and Staff roles screens.
export default function AllUsersPage({ user }) {
  const history = useHistory();
  const mayGrant = canPlatform(user, "staff.manage");
  const [people, setPeople] = useState(null);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("");
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let current = true;
    const timer = setTimeout(async () => {
      const res = await usersApi.list({ q: query, filter });
      if (!current) return;
      if (res.success) {
        setPeople(res.content);
        setError("");
      } else setError(res.msg || "Couldn't load the users.");
    }, 300);
    return () => {
      current = false;
      clearTimeout(timer);
    };
  }, [query, filter, reload]);

  const setRole = async (person, value) => {
    const role = value || null;
    if (role === staffRoleOf(person.staffRole)) return;
    const res = await usersApi.setStaffRole(person.id, role);
    if (!res.success) {
      setError(res.msg || "Couldn't change the bpExperts role.");
      return;
    }
    setToast(res.msg || "Saved.");
    setReload((n) => n + 1);
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, ...focusRing }}>
      <Typography variant="h5" component="h1" sx={{ fontWeight: 700 }}>
        All users
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        Everyone across workspaces. Workspace roles are set inside each workspace; the bpExperts role is set here.
      </Typography>

      <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} sx={{ mb: 2.5 }}>
        <TextField
          size="small"
          placeholder="Search by name, email or workspace"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon fontSize="small" /></InputAdornment> }}
          sx={{ maxWidth: { md: 360 } }}
        />
        <ToggleButtonGroup exclusive size="small" value={filter} onChange={(e, next) => next !== null && setFilter(next)} aria-label="Filter users" sx={{ flexWrap: "wrap" }}>
          {FILTERS.map((option) => (
            <ToggleButton key={option.key || "all"} value={option.key}>
              {option.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Stack>

      {error && (
        <Refusal sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Refusal>
      )}

      {!people ? (
        <ListSkeleton rows={6} height={60} />
      ) : people.length === 0 ? (
        <EmptyState
          icon={<GroupRoundedIcon />}
          title={query || filter ? "No one matches" : "No users yet"}
          action={
            query || filter ? (
              <Button
                onClick={() => {
                  setQuery("");
                  setFilter("");
                }}
              >
                Show everyone
              </Button>
            ) : null
          }
        >
          {query || filter
            ? "Clear the search or choose Everyone."
            : "People appear here once they are invited into a workspace."}
        </EmptyState>
      ) : (
        <Surface>
          {people.map((person, index) => (
            <Stack
              key={person.id}
              direction={{ xs: "column", md: "row" }}
              spacing={{ xs: 1, md: 2 }}
              alignItems={{ md: "center" }}
              sx={rowSx(index === 0)}
            >
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1.2, minWidth: 0 }}>
                <Avatar sx={{ width: 36, height: 36, bgcolor: brand.ink, color: brand.signal, fontSize: "0.8rem", fontWeight: 700 }}>{initials(person)}</Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 600, overflowWrap: "anywhere" }}>{fullName(person)}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>
                    {person.email}
                  </Typography>
                </Box>
              </Stack>

              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ flex: 1, minWidth: 0 }}>
                {person.workspaces.length === 0 ? (
                  <Typography variant="caption" color="text.secondary">
                    No workspace
                  </Typography>
                ) : (
                  person.workspaces.map((workspace) => (
                    <Chip
                      key={workspace.id || workspace.name}
                      size="small"
                      label={`${workspace.name}${workspaceRoleOf(workspace.role) === "admin" ? " · Admin" : ""}`}
                      onClick={workspace.id ? () => history.push(`/admin/workspaces/${encodeURIComponent(workspace.id)}`) : undefined}
                      sx={{ bgcolor: brand.canvas }}
                    />
                  ))
                )}
                {person.workspaces.length > 1 && (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`Belongs to ${person.workspaces.length} workspaces`}
                    sx={{ color: notice.wait.fg, bgcolor: notice.wait.bg, borderColor: notice.wait.line }}
                  />
                )}
                {person.expired && (
                  <Chip size="small" variant="outlined" label="Trial expired" sx={{ color: notice.fix.fg, bgcolor: notice.fix.bg, borderColor: notice.fix.line }} />
                )}
              </Stack>

              <Tooltip title={person.id === user?.id ? "Ask another bpExperts admin to change your own role." : ""}>
                <TextField
                  select
                  size="small"
                  label="bpExperts role"
                  value={staffRoleOf(person.staffRole) || ""}
                  disabled={!mayGrant || person.id === user?.id}
                  onChange={(e) => setRole(person, e.target.value)}
                  SelectProps={{ MenuProps: menuPaper, displayEmpty: true }}
                  InputLabelProps={{ shrink: true }}
                  sx={{ width: { xs: "100%", md: 190 } }}
                >
                  <MenuItem value="">None</MenuItem>
                  {STAFF_ROLES.map((role) => (
                    <MenuItem key={role.key} value={role.key}>
                      {role.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Tooltip>
            </Stack>
          ))}
        </Surface>
      )}

      <Toast notice={toast} onClose={() => setToast("")} />
    </Box>
  );
}
